import { parseContractEndDate } from '@order-management/dates';
import { formatSalesOrderNumber } from '@order-management/format';
import {
  SoftwareTableRow,
  SoftwareTitleForAssignment,
} from '@order-management/types';
import { useCallback, useMemo } from 'react';
import { Icon } from 'react-bulma-components';
import { FaTriangleExclamation } from 'react-icons/fa6';
import { PiWarningFill } from 'react-icons/pi';
import { Link, useOutletContext } from 'react-router-dom';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import { TableRowCheckboxTooltip } from '../../ui/table/table-row/table-row';
import { Column, RowSelection } from '../../ui/table/types';
import { ManageSoftwareLayoutContext } from '../manage-software-layout/manage-software-layout';

export type SoftwareTableProps = CustomPaginatedTableProps<SoftwareTableRow> & {
  includeSalesOrderColumn?: boolean;
  onSoftwareTitleClick: (
    software: SoftwareTitleForAssignment[],
    add: boolean
  ) => void;
  selectedSoftware: SoftwareTitleForAssignment[];
};

export function SoftwareAssignmentTable({
  includeSalesOrderColumn,
  onSoftwareTitleClick,
  selectedSoftware,
  ...tableProps
}: SoftwareTableProps) {
  const { selectedDevices, isInvalidSelection } =
    useOutletContext<ManageSoftwareLayoutContext>();

  const rowSelection: RowSelection = useMemo(() => {
    return {
      onChange: (selectedRowIds) => {
        selectedRowIds.forEach((id) => {
          const foundRow = selectedSoftware.find((sw) => sw.id === id);

          if (foundRow) {
            onSoftwareTitleClick([foundRow], false);
          } else {
            const newSelectedSoftware =
              tableProps.dataSource.find((sw) => sw.id === id) ||
              ({} as SoftwareTableRow);
            onSoftwareTitleClick([newSelectedSoftware], true);
          }
        });
      },
      selectedRowKeys: tableProps.dataSource
        .filter((v) => selectedSoftware.map((sw) => sw.id).includes(v.id))
        .map((v) => v.id),
    };
  }, [onSoftwareTitleClick, selectedSoftware, tableProps.dataSource]);

  const columns: Column<SoftwareTableRow>[] = useMemo(() => {
    const baseColumns: Column<SoftwareTableRow>[] = [
      {
        key: 'title',
        className: includeSalesOrderColumn ? 'is-width-30' : 'is-width-45',
        render: (el) => {
          const notEnoughSeats = el.seats.available < selectedDevices.length;
          const repeatedSoftwareTitle =
            selectedSoftware.some((software) => software.id === el.id) &&
            selectedSoftware.some(
              (software) =>
                software.licensingProductGroup.id ===
                  el.licensingProductGroup.id &&
                software.contractEndDate !== el.contractEndDate
            );
          return (
            <>
              {(notEnoughSeats || repeatedSoftwareTitle) && (
                <Icon color="danger-dark">
                  <PiWarningFill />
                </Icon>
              )}
              <span className="ml-1">
                {el.licensingProductGroup.displayName}
              </span>
            </>
          );
        },
        title: 'Software title',
        sortable: true,
      },
      {
        key: 'contract_end_date',
        className: 'is-width-15',
        title: 'Contract end date',
        render: (el) => parseContractEndDate(el.contractEndDate),
        sortable: true,
      },
      {
        key: 'available',
        className: 'is-width-15',
        render: (el) => `${el.seats.available}`,
        title: 'Available seats',
        sortable: true,
      },
      {
        key: 'type',
        className: 'is-width-10',
        dataIndex: 'type',
        title: 'Software type',
        sortable: false,
      },
    ];

    const salesOrderColumn: Column<SoftwareTableRow> = {
      key: 'so_number',
      className: 'is-width-15',
      title: 'Sales order',
      render: (el) => (
        <Link to={`/sales-orders/${el.salesOrder.id}`}>
          {formatSalesOrderNumber(el.salesOrder.number)}
        </Link>
      ),
      sortable: true,
    };

    if (includeSalesOrderColumn) {
      baseColumns.splice(1, 0, salesOrderColumn);
    }

    return baseColumns;
  }, [includeSalesOrderColumn, selectedDevices.length, selectedSoftware]);

  const isDisabledSelection = useCallback((el: SoftwareTableRow): boolean => {
    return !el.availableToAssign;
  }, []);

  const checkboxTooltipMessage = useCallback(
    (el: SoftwareTableRow): TableRowCheckboxTooltip => {
      const tooltipMessage: TableRowCheckboxTooltip = {
        message: '',
        backgroundColor: '',
      };

      if (!el.availableToAssign) {
        tooltipMessage.message = (
          <div className="is-flex is-align-items-center">
            <Icon color="danger-dark" mr={1}>
              <FaTriangleExclamation />
            </Icon>
            <span className="is-size-7">
              {`Software purchased before ${new Date(
                el.availableFromDate
              ).toLocaleDateString()} cannot be assigned using
            Management Hub. Please contact support.`}
            </span>
          </div>
        );
        tooltipMessage.backgroundColor = '#FEECF0';
      }
      return tooltipMessage;
    },
    []
  );

  return (
    <PaginatedTable
      {...tableProps}
      rowKey={'id'}
      columns={columns}
      rowSelection={rowSelection}
      rowLabel={(el) => el.licensingProductGroup.displayName}
      isInvalidSelection={isInvalidSelection}
      isDisabledSelection={isDisabledSelection}
      checkboxTooltip={checkboxTooltipMessage}
    />
  );
}

export default SoftwareAssignmentTable;
